import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Truck, Save, AlertTriangle, Box } from "lucide-react";

const STATE_HINTS = ["Maharashtra", "Karnataka", "Delhi", "Tamil Nadu", "Gujarat", "Telangana", "Kerala", "West Bengal", "Uttar Pradesh", "Rajasthan"];

const AdminShipping = () => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/shipping").then((r) => setData(r.data)).catch(() => toast.error("Load failed"));
  useEffect(() => { load(); }, []);

  const setPickup = (k, v) => setData({ ...data, pickup: { ...data.pickup, [k]: v } });
  const setPackage = (k, v) => setData({ ...data, package: { ...data.package, [k]: Number(v) || 0 } });

  const save = async (section) => {
    setSaving(true);
    try {
      const body = section === "pickup" ? { pickup: data.pickup } : { package: data.package };
      await api.patch("/admin/shipping", body);
      toast.success("Saved — Xpressbees shipments will use this address from now on");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!data) return <p className="text-cream/75">Loading…</p>;

  const required = ["name", "address", "city", "state", "pincode", "phone"];
  const missing = required.filter((k) => !(data.pickup?.[k] || "").trim());

  return (
    <div data-testid="admin-shipping-page">
      <p className="eyebrow text-cream/65 mb-1">Logistics</p>
      <h1 className="font-hero text-3xl text-cream flex items-center gap-3 mb-2">
        <Truck size={26} /> Shipping settings
      </h1>
      <p className="text-cream/65 text-sm mb-8 max-w-xl">
        These are sent to Xpressbees every time you click "Book shipment" on an order. All required fields must be set before any shipment can be created.
      </p>

      {missing.length > 0 && (
        <div className="bg-rose/15 border border-rose/30 rounded-2xl p-4 mb-6 max-w-4xl flex gap-3" data-testid="shipping-missing-warning">
          <AlertTriangle size={18} className="text-rose flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-cream">Pickup address incomplete — Xpressbees will reject shipments</p>
            <p className="text-sm text-cream/70 mt-1">Missing: {missing.map((m) => <span key={m} className="font-mono text-rose mr-1.5">{m}</span>)}</p>
          </div>
        </div>
      )}

      <section className="bg-white rounded-2xl border border-border p-6 max-w-4xl mb-6" data-testid="pickup-form">
        <p className="eyebrow mb-1">Pickup address (your warehouse / studio)</p>
        <p className="text-xs text-charcoal/60 mb-5">This is where the courier picks up parcels. Most jewelry brands use their studio or office address.</p>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="eyebrow block mb-1">Warehouse name <span className="normal-case tracking-normal text-charcoal/60">(short label, no spaces)</span></label>
            <Input value={data.pickup.warehouse_name || ""} onChange={(e) => setPickup("warehouse_name", e.target.value.replace(/\s+/g, "-").toLowerCase())} placeholder="rieave-mumbai" data-testid="pickup-warehouse" />
          </div>
          <div>
            <label className="eyebrow block mb-1">Contact name <span className="text-rose">*</span></label>
            <Input value={data.pickup.name || ""} onChange={(e) => setPickup("name", e.target.value)} placeholder="Rieave Studio" data-testid="pickup-name" />
          </div>
          <div className="md:col-span-2">
            <label className="eyebrow block mb-1">Address line 1 <span className="text-rose">*</span></label>
            <Input value={data.pickup.address || ""} onChange={(e) => setPickup("address", e.target.value)} placeholder="401, Hill Crest, Andheri West" data-testid="pickup-address" />
          </div>
          <div className="md:col-span-2">
            <label className="eyebrow block mb-1">Address line 2 <span className="normal-case tracking-normal text-charcoal/60">(landmark / unit / block)</span></label>
            <Input value={data.pickup.address_2 || ""} onChange={(e) => setPickup("address_2", e.target.value)} data-testid="pickup-address2" />
          </div>
          <div>
            <label className="eyebrow block mb-1">City <span className="text-rose">*</span></label>
            <Input value={data.pickup.city || ""} onChange={(e) => setPickup("city", e.target.value)} placeholder="Mumbai" data-testid="pickup-city" />
          </div>
          <div>
            <label className="eyebrow block mb-1">State <span className="text-rose">*</span></label>
            <Input list="state-hints" value={data.pickup.state || ""} onChange={(e) => setPickup("state", e.target.value)} placeholder="Maharashtra" data-testid="pickup-state" />
            <datalist id="state-hints">{STATE_HINTS.map(s => <option key={s} value={s} />)}</datalist>
          </div>
          <div>
            <label className="eyebrow block mb-1">Pincode <span className="text-rose">*</span></label>
            <Input value={data.pickup.pincode || ""} onChange={(e) => setPickup("pincode", e.target.value.replace(/[^0-9]/g, "").slice(0, 6))} placeholder="400053" data-testid="pickup-pincode" />
          </div>
          <div>
            <label className="eyebrow block mb-1">Phone <span className="text-rose">*</span> <span className="normal-case tracking-normal text-charcoal/60">(10 digits, no +91)</span></label>
            <Input value={data.pickup.phone || ""} onChange={(e) => setPickup("phone", e.target.value.replace(/[^0-9]/g, "").slice(0, 10))} placeholder="9876543210" data-testid="pickup-phone" />
          </div>
        </div>

        <button onClick={() => save("pickup")} disabled={saving} className="btn-primary mt-6 inline-flex items-center gap-2" data-testid="save-pickup">
          <Save size={14} /> {saving ? "Saving…" : "Save pickup address"}
        </button>
      </section>

      <section className="bg-white rounded-2xl border border-border p-6 max-w-4xl" data-testid="package-form">
        <div className="flex items-center gap-2 mb-1">
          <Box size={16} className="text-rose" />
          <p className="eyebrow">Default package dimensions</p>
        </div>
        <p className="text-xs text-charcoal/60 mb-5">Used by Xpressbees to compute volumetric weight. For jewelry these are usually small.</p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="eyebrow block mb-1">Weight (g)</label>
            <Input type="number" min={50} value={data.package.weight_g || 0} onChange={(e) => setPackage("weight_g", e.target.value)} data-testid="pkg-weight" />
          </div>
          <div>
            <label className="eyebrow block mb-1">Length (cm)</label>
            <Input type="number" min={1} value={data.package.length_cm || 0} onChange={(e) => setPackage("length_cm", e.target.value)} data-testid="pkg-length" />
          </div>
          <div>
            <label className="eyebrow block mb-1">Breadth (cm)</label>
            <Input type="number" min={1} value={data.package.breadth_cm || 0} onChange={(e) => setPackage("breadth_cm", e.target.value)} data-testid="pkg-breadth" />
          </div>
          <div>
            <label className="eyebrow block mb-1">Height (cm)</label>
            <Input type="number" min={1} value={data.package.height_cm || 0} onChange={(e) => setPackage("height_cm", e.target.value)} data-testid="pkg-height" />
          </div>
        </div>

        <button onClick={() => save("package")} disabled={saving} className="btn-primary mt-6 inline-flex items-center gap-2" data-testid="save-package">
          <Save size={14} /> {saving ? "Saving…" : "Save package defaults"}
        </button>
      </section>
    </div>
  );
};

export default AdminShipping;
